import { ENDPOINT } from '~/constants';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type ReprocessingInvoiceEmailParams } from './types';
import { zodReprocessingInvoiceEmailEndpointSchema } from './validations';

/**
 * Download an email that is part of an invoice requiring reprocessing based on the process ID and azure ID.
 * @param params - The parameters for fetching the reprocessing invoice email.
 * @returns The email content as Blob.
 * @throws Error if the API request fails.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoice/operation/get_process_mail_invoice_process__process_id__mail__azure_id__get
 */
export const fetchReprocessingInvoiceEmail = async ({
  azureId,
  processId,
}: ReprocessingInvoiceEmailParams): Promise<Blob> => {
  try {
    const response = await vestigasApi
      .get(ENDPOINT.INVOICE_REPROCESSING.GET_EMAIL(processId, azureId), {
        validate: { pathSegments: zodReprocessingInvoiceEmailEndpointSchema },
      })
      .blob();

    return response;
  } catch (error) {
    log.error({
      message: 'Error fetching reprocessing invoice email',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(
            ($) => $.fetchReprocessingInvoiceEmail.error404,
            { ns: 'data' },
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            ($) => $.fetchReprocessingInvoiceEmail.error422,
            { ns: 'data' },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.fetchReprocessingInvoiceEmail.error.generic,
            { ns: 'data' },
          );
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
